// src/components/UrgentOrders.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Input,
  Select,
  Spin,
  Empty,
  message,
  Modal,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

// ✅ SAME PDF LOGIC
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawDocket, drawCustomerDocket } from "../utils/docketTemplate";

const { Option } = Select;
const { confirm } = Modal;

const UrgentOrders = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    fetchUrgentOrders();
  }, []);

  const fetchUrgentOrders = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/urgent-orders");
      if (res.data?.success) {
        setData(res.data.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("fetchUrgentOrders error:", err);
      message.error("Failed to load urgent orders");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate / view or download a docket PDF for an urgent request.
   * - mode: "view" => open PDF in new tab
   *         "download" => save as file
   */
  const printUrgentDocket = async (urgentRequestId, mode = "view") => {
    try {
      const res = await axiosInstance.get(
        `/admin/urgent-orders/${urgentRequestId}/print-data`
      );

      const orders = res.data?.data || [];
      if (!orders.length) {
        message.error("No orders found for this urgent request");
        return;
      }

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "A4",
      });

      // group by customer
      const byCustomer = new Map();
      orders.forEach((o) => {
        const c = o.user?.company_name || o.company_name || "Customer";
        if (!byCustomer.has(c)) byCustomer.set(c, []);
        byCustomer.get(c).push(o);
      });

      let pageIndex = 0;
      for (const [, list] of byCustomer) {
        if (pageIndex > 0) doc.addPage();

        if (list.length === 1) {
          drawDocket(doc, list[0]);
        } else {
          drawCustomerDocket(doc, list);
        }

        pageIndex++;
      }

      // ensure page numbers / footer
      try {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          const w = doc.internal.pageSize.getWidth();
          const h = doc.internal.pageSize.getHeight();
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text(`Page ${i} of ${pageCount}`, w / 2, h - 16, {
            align: "center",
          });
          doc.setTextColor(0);
        }
      } catch (e) {
        // silent
      }

      if (mode === "view") {
        const blobUrl = doc.output("bloburl");
        window.open(blobUrl, "_blank");
      } else {
        doc.save(`Urgent-Docket-${urgentRequestId}.pdf`);
      }
    } catch (e) {
      console.error("printUrgentDocket error:", e);
      message.error("Failed to generate urgent docket");
    }
  };

  /* Approve / Reject */
  const handleStatusUpdate = (urgentRequestId, status) => {
    confirm({
      title: `Are you sure you want to ${status} this urgent request?`,
      icon: status === "approved" ? <CheckCircleOutlined /> : <CloseCircleOutlined />,
      okText: "Yes",
      okButtonProps: { danger: status === "rejected" },
      onOk: async () => {
        setActionLoading(urgentRequestId);
        try {
          await axiosInstance.put(`/admin/urgent-orders/${urgentRequestId}`, { status });
          message.success(`Urgent request ${status}`);
          fetchUrgentOrders();
        } catch (err) {
          console.error("handleStatusUpdate error:", err);
          message.error("Failed to update urgent status");
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  /* filtered rows used in UI & CSV export */
  const filteredData = useMemo(() => {
    let rows = Array.isArray(data) ? data.slice() : [];

    if (statusFilter) {
      rows = rows.filter((r) => r.status === statusFilter);
    }

    if (search) {
      const s = String(search).toLowerCase().trim();
      rows = rows.filter(
        (r) =>
          String(r.urgentRequestId || "").toLowerCase().includes(s) ||
          (r.company_name || "").toLowerCase().includes(s)
      );
    }

    return rows;
  }, [data, search, statusFilter]);

  /* Export filtered data to CSV */
  const exportCsv = () => {
    if (!filteredData.length) {
      message.info("No rows to export");
      return;
    }

    const headers = [
      "Urgent Request ID",
      "Total Orders",
      "Customer",
      "Reason",
      "Status",
    ];

    const rows = filteredData.map((r) => [
      r.urgentRequestId || "",
      r.totalOrders ?? "",
      r.company_name || (r.user?.company_name || "") || "",
      r.reason || r.urgentReason || "",
      r.status || "",
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) =>
          row
            .map((cell) => {
              if (cell == null) return "";
              const s = String(cell).replace(/"/g, '""');
              return `"${s}"`;
            })
            .join(",")
        )
        .join("\r\n") || "";

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `urgent_requests_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: "Orders",
      render: (_, rec) =>
        rec.totalOrders === 1 ? <strong>1 Order</strong> : <Tag color="blue">{rec.totalOrders} Orders</Tag>,
    },
    {
      title: "Customer",
      render: (_, rec) => (rec.totalCustomers > 1 ? <Tag>Multiple customers</Tag> : rec.company_name || "—"),
    },
    {
      title: "Urgent Reason",
      dataIndex: "reason",
      render: (text, rec) => (
        <Tooltip title={text || rec.urgentReason || "—"}>
          <span style={{ maxWidth: 260, display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {text || rec.urgentReason || "—"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) =>
        status === "approved" ? (
          <Tag color="success">Approved</Tag>
        ) : status === "rejected" ? (
          <Tag color="error">Rejected</Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            Pending
          </Tag>
        ),
    },
    {
      title: "Actions",
      render: (_, rec) => (
        <Space>
          <Tooltip title="View docket">
            <Button icon={<EyeOutlined />} onClick={() => printUrgentDocket(rec.urgentRequestId, "view")} />
          </Tooltip>

          <Tooltip title="Download PDF">
            <Button icon={<DownloadOutlined />} onClick={() => printUrgentDocket(rec.urgentRequestId, "download")} />
          </Tooltip>

          {rec.status === "pending" && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="primary"
                  loading={actionLoading === rec.urgentRequestId}
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleStatusUpdate(rec.urgentRequestId, "approved")}
                />
              </Tooltip>

              <Tooltip title="Reject">
                <Button
                  danger
                  loading={actionLoading === rec.urgentRequestId}
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleStatusUpdate(rec.urgentRequestId, "rejected")}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">🚨 Urgent Orders</h1>
                <p className="text-gray-600">Review and manage urgent order requests</p>
              </div>

              <div>
                <Space>
                  <Button onClick={fetchUrgentOrders}>Refresh</Button>
                  <Button onClick={exportCsv}>Export CSV</Button>
                </Space>
              </div>
            </div>
          </div>

          {/* Filters - uniform heights */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6 uniform-fields">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <SearchOutlined className="mr-2" /> Search Docket ID / Company
                </label>
                <Input
                  placeholder="e.g., URG-2026-001 or company name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  allowClear
                  className="w-full"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Status</label>
                <Select
                  placeholder="Status"
                  allowClear
                  value={statusFilter || undefined}
                  onChange={(v) => setStatusFilter(v || null)}
                  className="w-full"
                >
                  <Option value="pending">Pending</Option>
                  <Option value="approved">Approved</Option>
                  <Option value="rejected">Rejected</Option>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              rowKey="urgentRequestId"
              dataSource={filteredData}
              columns={columns}
              pagination={{ pageSize: 20 }}
              locale={{
                emptyText: <Empty description="No urgent orders found" />,
              }}
            />
          </div>
        </div>
      </div>

      {/* Scoped styles to ensure uniform control heights match other pages */}
      <style jsx>{`
  /* 🔥 UNIFORM BIGGER HEIGHT CONTROLS */
  .uniform-fields :global(.ant-input),
  .uniform-fields :global(.ant-input-affix-wrapper),
  .uniform-fields :global(.ant-select-selector),
  .uniform-fields :global(.ant-btn) {
    height: 48px !important;
    min-height: 48px !important;
    line-height: 48px !important;
    box-sizing: border-box !important;
    display: flex !important;
    align-items: center !important;
  }

  /* Select inner alignment */
  .uniform-fields :global(.ant-select-selection-item),
  .uniform-fields :global(.ant-select-selection-placeholder) {
    line-height: 48px !important;
    display: flex !important;
    align-items: center !important;
  }

  .uniform-fields :global(.ant-select-arrow) {
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  /* Summary box SAME HEIGHT */
  .uniform-fields .summary-box {
    height: 44px !important;
    min-height: 44px !important;
    display: flex;
    align-items: center;
    padding: 0 14px !important;
  }

  /* Button alignment */
  .uniform-fields :global(.ant-btn) {
    padding: 0 16px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`}</style>



    </Spin>
  );
};

export default UrgentOrders;
